import { useFormik } from 'formik';
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';
import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only';

import { colors as colorsCommon } from '@components/common';
import { SampleCard, AutoIncrease } from '@universal-components/icons';

import AutoPaySVG from '../../../assets/icons/bullet-autopay.svg';
import TempCreditLimitSVG from '../../../assets/icons/bullet-temp-credit-limit.svg';
import DownloadArrowSVG from '../../../assets/icons/download-icon.svg';
import StepOneSvg from '../../../assets/icons/number-1-orange.svg';
import StepTwoSvg from '../../../assets/icons/number-2-orange.svg';
import StepThreeSvg from '../../../assets/icons/number-3-orange.svg';
import { CreditCardApplicationContext } from '../../../CreditCardApplicationContext';
import { ErrorContext } from '../../../ErrorContext';
import { ActionButton } from 'src/commons/components/Button';
import Card, { ErrorCard, LoadingCard } from 'src/commons/components/Card';
import { CheckBox } from 'src/commons/components/CheckBox';
import Heading from 'src/commons/components/Heading';
import Highlighter from 'src/commons/components/Highlighter';
import {
  SchumerLabel,
  SchumerDownloadLink,
  SchumerBoxContainer,
  SchumerNotice,
} from 'src/commons/components/Schumer';
import {
  ApplicationStatus,
  ApplicationWorkflowEvent,
  colors,
  LegalDocuments,
  MOBILE_BREAKPOINT,
  PageNames,
  UNAUTHORIZED_MESSAGE,
} from 'src/commons/constants';
import { GraphQLResponse } from 'src/commons/data/graphql';
import {
  getCreditCardApplicationLegalDocument,
  processCreditCardApplicationWorkflowEvent,
} from 'src/commons/data/queries';
import {
  formatNumberWithCommas,
  capitalizeFirstName,
} from 'src/commons/utilities/formatting';
import {
  logError,
  nrTrackCreditCardApplicationWorkflowEvent,
  pushToDataLayer,
} from 'src/commons/utilities/logging';
import { prefixRoute } from 'src/commons/utilities/routes';

// Interface --------------------------------------------------------------------------------------
interface ConditionalStyledProps {
  isConditional: boolean;
}

// #region -- Styles ------------------------------------------------------------------------------
// Need to reduce padding to 0.5rem to accommodate gradient background
const GradientCard = styled(Card)`
  padding: 0.5rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0.5rem;
  }
`;
GradientCard.displayName = 'GradientCard';

const CongratsSection = styled.div`
  width: 100%;
  padding: 3rem 0 0;
  background: radial-gradient(
    71.74% 119.35% at 50% 100%,
    rgba(255, 247, 240, 0.07) 0%,
    rgba(254, 120, 0, 0.08) 100%
  );
  background-repeat: no-repeat;
  background-size: 100% 13.5625rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 1.5rem 0 0;
  }
`;
CongratsSection.displayName = 'CongratsSection';

// Add 0.5rem padding so content of GradientCard is consistent with other funnel pages
const StyledForm = styled.form<ConditionalStyledProps>`
  max-width: 25rem;
  padding: 0 0.5rem 0.5rem;
  display: flex;
  flex-direction: column;
  padding-top: ${(p: ConditionalStyledProps) =>
    p.isConditional ? '1rem' : '0.5rem'};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-top: ${(p: ConditionalStyledProps) =>
      p.isConditional ? '1rem' : '0'};
  }
`;
StyledForm.displayName = 'OfferForm';

const HeadingSection = styled.div`
  padding: 0.25rem 0 0;
  text-align: center;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0.3125rem 0 0;
  }
`;
HeadingSection.displayName = 'HeadingSection';

const StyledHeading = styled(Heading)<ConditionalStyledProps>`
  margin: 0.375rem 0 0;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    max-width: 16rem;
    margin: 0.6875rem auto 0;
  }
  ${(p: ConditionalStyledProps) =>
    p.isConditional &&
    `
    line-height: 2rem;
    margin: 0;
    padding: 0 7rem;
    @media (max-width: ${MOBILE_BREAKPOINT}) {
      max-width: 16.75rem;
      margin: 0.6875rem auto 0;
      padding: 0;
    }
  `};
`;
StyledHeading.displayName = 'StyledHeading';

const WrapperSampleCard = styled.div`
  position: relative;
  height: 8.3125rem;
  width: 12.875rem;
  margin: 1.5rem auto 2.8125rem;
  border-radius: 0.59875rem;
  overflow: hidden;
  background-color: ${colorsCommon.orange.orange9};
  box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.12);
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 1.375rem auto 2.3125rem;
  }
`;
WrapperSampleCard.displayName = 'WrapperSampleCard';

const CardContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  padding: 1rem 1rem 0.875rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

CardContent.displayName = 'CardContent';

const SampleCardLineAmount = styled.p`
  vertical-align: bottom;
  font-size: 2.25rem;
  color: ${colors.TEXT_SECONDARY};
  font-weight: 300;
  margin-bottom: 0.375rem;
`;
SampleCardLineAmount.displayName = 'SampleCardLineAmount';

const SampleCardParagraph = styled.p`
  margin: 0 0 0 0.125rem;
  padding: 0.125rem 0 0;
  text-transform: uppercase;
  font-size: 0.6875rem;
  letter-spacing: 0.03125rem;
  color: ${colors.TEXT_SECONDARY};
`;
SampleCardParagraph.displayName = 'SampleCardParagraph';

const StyledBulletList = styled.ul`
  width: fit-content;
  margin: 0 auto;
`;
StyledBulletList.displayName = 'StyledBulletList';

const StyledBulletItem = styled.li`
  margin: 1.375rem 1.125rem;
  display: flex;
  align-items: center;
  color: ${colors.TEXT_PRIMARY};
  & > p {
    margin: 0 0 0 1.0625rem;
    max-width: 20rem;
    line-height: 1.5rem;
  }
  & > p > span {
    font-weight: bold;
  }
  a:last-child {
    margin: 0;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 1.625rem 0.625rem;
    a:last-child {
      margin: 0;
    }
  }
`;
StyledBulletItem.displayName = 'StyledBulletItem';

const SchumerSection = styled.div`
  width: 100%;
  max-width: 25rem;
  max-height: 18.5rem;
  overflow: hidden;
`;
SchumerSection.displayName = 'SchumerSection';

const EsignSection = styled.div`
  margin: 3.125rem 0 2.5rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0 0 1.625rem;
  }
`;
EsignSection.displayName = 'EsignSection';

const SchumerESignNotice = styled(SchumerNotice)`
  margin: 2.5rem 0 1rem;
  font-weight: 500;
  font-size: 1rem;
`;
SchumerESignNotice.displayName = 'SchumerESignNotice';

// Necessary text container for Esign checkbox screen reader labelling
const SchumerEsignParagraphSection = styled.div``;
SchumerEsignParagraphSection.displayName = 'SchumerEsignParagraphSection';

const SchumerEsignParagraph = styled.p`
  padding: 0 0 0.5rem;
  line-height: 1.375rem;
  font-size: 1rem;
  color: ${colors.TEXT_PRIMARY};
`;
SchumerEsignParagraph.displayName = 'SchumerEsignParagraph';

const SubmitButton = styled(ActionButton)`
  margin: 0 0 4.5625rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0 0 1.625rem;
  }
`;
SubmitButton.displayName = 'SubmitButton';

const ClickableSup = styled.a`
  cursor: pointer;
  position: absolute;
  padding: 0 0 0 0.0625rem;
  color: ${colors.TEXT_PRIMARY};
  text-decoration: none;
`;
// #endregion

// Component --------------------------------------------------------------------------------------
const OfferBullets = (props: ConditionalStyledProps) => {
  const { isConditional } = props;
  const { onSuperscriptClick, onSuperscriptEnter } = useContext(
    CreditCardApplicationContext
  );

  if (isConditional) {
    // Render two-step bullet list
    return (
      <StyledBulletList>
        <StyledBulletItem>
          <div>
            <StepOneSvg />
          </div>
          <p>Review this offer</p>
        </StyledBulletItem>
        <StyledBulletItem>
          <div>
            <StepTwoSvg />
          </div>
          <p>Agree to the following terms</p>
        </StyledBulletItem>
        <StyledBulletItem>
          <div>
            <StepThreeSvg />
          </div>
          <p>Connect your bank to get verified</p>
        </StyledBulletItem>
      </StyledBulletList>
    );
  }

  // Otherwise, render one-step bullet list
  return (
    <StyledBulletList>
      <StyledBulletItem>
        <div>
          <AutoIncrease />
        </div>
        <p id="footnote-source-1">
          We’ll automatically review your account for credit line increases
          <ClickableSup
            onClick={(e) => onSuperscriptClick(e, 1)}
            onKeyDown={(e) => onSuperscriptEnter(e, 1)}
            id="sup-1"
            href="#footnote-detail-1"
            aria-label="footnote-1"
            aria-describedby="footnotes-describer"
            role="link"
          >
            ¹
          </ClickableSup>
        </p>
      </StyledBulletItem>
      <StyledBulletItem>
        <div>
          <AutoPaySVG />
        </div>
        <p id="footnote-source-2">
          Set up AutoPay and pay <span>$0 annual fee</span> for the first year,
          we’ve got you covered
          <ClickableSup
            onClick={(e) => onSuperscriptClick(e, 2)}
            onKeyDown={(e) => onSuperscriptEnter(e, 2)}
            id="sup-2"
            href="#footnote-detail-2"
            aria-label="footnote-2"
            aria-describedby="footnotes-describer"
            role="link"
          >
            ²
          </ClickableSup>
        </p>
      </StyledBulletItem>
      <StyledBulletItem>
        <div>
          <TempCreditLimitSVG />
        </div>
        <p>
          Instantly access 50% of your credit line while your card is being
          delivered
        </p>
      </StyledBulletItem>
    </StyledBulletList>
  );
};

// Main offer page component
export const Offer = () => {
  // State ----------------------------------------------------------------------------------------
  const [showOfferFetchError, setShowOfferFetchError] = useState(false);
  const [fetchingOfferDocs, setFetchingOfferDocs] = useState(true);
  const [cardHolderAgreementName, setCardHolderAgreementName] = useState('');
  const [signedViewUrl, setSignedViewUrl] = useState('');
  const [signedDownloadUrl, setSignedDownloadUrl] = useState('');
  const [cardHolderAgreementVersion, setCardHolderAgreementVersion] =
    useState(0);

  // Context --------------------------------------------------------------------------------------
  const {
    creditCardApplication,
    fetchingApplication,
    fetchAndResumeCreditCardApplication,
    showApplicationFetchError,
  } = useContext(CreditCardApplicationContext);
  const { setShowErrorModal } = useContext(ErrorContext);
  const { offerCheck, applicant, applicationStatus } =
    creditCardApplication || {};
  const { offer } = offerCheck || {};
  const { firstName, lastName } = applicant;
  const { creditLimit, creditApr, conditionalOnCashflowVerification } =
    offer || {};

  // Constants ------------------------------------------------------------------------------------
  const navigate = useNavigate();
  const { applicationId } = useParams();
  const schumerNoticeCopy =
    'By checking the box below you agree by electronic signature to and acknowledge the following:';

  // Condition to render Schumer section content checkbox
  const isPendingUnconditionalOfferAccept = () =>
    applicationStatus &&
    applicationStatus === ApplicationStatus.PENDING_UNCONDITIONAL_OFFER_ACCEPT;

  // Update application status with workflow event 'OFFER_ACCEPTED'
  const handleOfferAcceptedWorkflowEvent =
    async (): Promise<GraphQLResponse> => {
      const offerAcceptedEvent = ApplicationWorkflowEvent.OFFER_ACCEPTED;
      const offerAcceptedEventAttributes = {
        legalDocumentsToSave: [
          {
            name: cardHolderAgreementName,
            version: cardHolderAgreementVersion,
          },
        ],
      };
      return processCreditCardApplicationWorkflowEvent(
        applicationId,
        offerAcceptedEvent,
        offerAcceptedEventAttributes
      );
    };

  // Formik
  const formik = useFormik({
    initialValues: {
      schumerEsign: false,
    },
    validationSchema: Yup.object().shape({
      schumerEsign: Yup.boolean().oneOf(
        [true],
        'Please review and agree to the document above'
      ),
    }),

    onSubmit: async () => {
      try {
        const { data } = await handleOfferAcceptedWorkflowEvent();
        nrTrackCreditCardApplicationWorkflowEvent(data);
        // Route to next page in the funnel if the OFFER_ACCEPTED event was processed successfully
        if (
          data &&
          data.processCreditCardApplicationWorkflowEvent &&
          data.processCreditCardApplicationWorkflowEvent.applicationStatus
        ) {
          const { applicationStatus: nextStatus } =
            data.processCreditCardApplicationWorkflowEvent;
          if (
            nextStatus === ApplicationStatus.PENDING_UNCONDITIONAL_SUBMISSION
          ) {
            // Route to AutoPay if the status is now PENDING_UNCONDITIONAL_SUBMISSION
            navigate(prefixRoute(`/application/${applicationId}/auto-pay`));
          } else {
            // Route to ConnectToBank if the status is now PENDING_BANK_CONNECT_AUTHORIZATION
            navigate(
              prefixRoute(`/application/${applicationId}/connect-to-bank`)
            );
          }
        } else {
          setShowErrorModal(true);
        }
      } catch (err: any) {
        if (err.message !== UNAUTHORIZED_MESSAGE) {
          setShowErrorModal(true);
          logError(err, {
            errorName: err.name || 'PageError',
            errorMessage: err.message || 'Unexpected error occurred',
            component: 'Offer',
            function: 'onSubmit',
          });
        }
      }
    },
  });

  // Hooks ----------------------------------------------------------------------------------------

  useEffect(() => {
    const abortController = new AbortController();
    // Fetch the application
    fetchAndResumeCreditCardApplication(PageNames.OFFER);

    // Fetch the offer
    const fetchOffer = async () => {
      try {
        const { data } = await getCreditCardApplicationLegalDocument(
          applicationId,
          LegalDocuments.CARDHOLDER_AGREEMENT
        );
        // Then set the offer in state
        if (!abortController.signal.aborted) {
          if (data.getCreditCardApplicationLegalDocument) {
            const offerRes = data.getCreditCardApplicationLegalDocument;
            setCardHolderAgreementName(offerRes.name);
            setSignedViewUrl(offerRes.signedViewUrl);
            setSignedDownloadUrl(offerRes.signedDownloadUrl);
            setCardHolderAgreementVersion(offerRes.version);
          } else {
            setShowOfferFetchError(true);
          }
          setFetchingOfferDocs(false);
        }
      } catch (err: any) {
        if (
          !abortController.signal.aborted &&
          err.message !== UNAUTHORIZED_MESSAGE
        ) {
          // Show the blocking error
          setShowOfferFetchError(true);
          logError(err, {
            errorName: err.name || 'PageError',
            errorMessage: err.message || 'Unexpected error occurred',
            component: 'Offer',
            function: 'fetchOffer',
          });
        }
      }
    };
    fetchOffer();

    // Unsubscribe
    return () => abortController.abort();
  }, []);

  // Push credit_limit to dataLayer for Google Analytics 4 tagging
  useEffect(() => {
    if (creditLimit) pushToDataLayer({ credit_limit: creditLimit });
  }, [creditLimit]);

  // Push verification_type to dataLayer for Google Analytics 4 tagging
  useEffect(() => {
    if (typeof conditionalOnCashflowVerification === 'boolean') {
      const verificationType = conditionalOnCashflowVerification
        ? 'Two-step'
        : 'One-step';
      pushToDataLayer({ verification_type: verificationType });
    }
  }, [conditionalOnCashflowVerification]);

  // Push rate to dataLayer for Google Analytics 4 tagging
  useEffect(() => {
    if (creditApr) pushToDataLayer({ rate: creditApr });
  }, [creditApr]);

  // Render ---------------------------------------------------------------------------------------

  if (showOfferFetchError || showApplicationFetchError) {
    return <ErrorCard />;
  }

  if (fetchingApplication || fetchingOfferDocs) {
    return <LoadingCard />;
  }

  const styledHeadingId = isPendingUnconditionalOfferAccept()
    ? 'offer-styled-heading-unconditional'
    : 'offer-styled-heading-conditional';

  return (
    <GradientCard id="offer-page-card">
      <CongratsSection>
        <HeadingSection>
          {isPendingUnconditionalOfferAccept() && (
            <Highlighter id="congrats-heading">
              &nbsp;Congrats&nbsp;
              {firstName && capitalizeFirstName(firstName)}
              !&nbsp;
            </Highlighter>
          )}
          <StyledHeading
            data-testid={styledHeadingId}
            isConditional={!isPendingUnconditionalOfferAccept()}
          >
            {isPendingUnconditionalOfferAccept()
              ? 'Check out your credit card details'
              : `You${String.fromCharCode(
                  8217
                )}re just a few quick steps away from getting a decision`}
          </StyledHeading>
        </HeadingSection>
        <WrapperSampleCard id="credit-limit-container">
          <SampleCard
            style={{
              position: 'absolute',
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />
          <CardContent>
            <SampleCardLineAmount id="credit-limit-amount">
              ${creditLimit && formatNumberWithCommas(creditLimit)}
            </SampleCardLineAmount>
            <SampleCardParagraph id="pre-approved-credit">
              {isPendingUnconditionalOfferAccept()
                ? 'Your credit limit'
                : 'Yours upon successful bank verification'}
            </SampleCardParagraph>
          </CardContent>
        </WrapperSampleCard>
        <OfferBullets isConditional={!isPendingUnconditionalOfferAccept()} />
      </CongratsSection>
      <StyledForm
        id="offer-form"
        isConditional={!isPendingUnconditionalOfferAccept()}
        onSubmit={(e) => {
          formik.handleSubmit(e);
        }}
      >
        <SchumerSection>
          <SchumerLabel>
            {isPendingUnconditionalOfferAccept()
              ? 'Cardholder Agreement'
              : 'Terms & Conditions*'}
            <SchumerDownloadLink
              id="download"
              href={signedDownloadUrl}
              aria-label="Download Terms & Conditions"
              download="Terms & Conditions"
            >
              Download
              <DownloadArrowSVG />
            </SchumerDownloadLink>
          </SchumerLabel>
          <SchumerBoxContainer src={signedViewUrl} />
        </SchumerSection>

        <EsignSection>
          {!isPendingUnconditionalOfferAccept() && (
            <SchumerESignNotice>{schumerNoticeCopy}</SchumerESignNotice>
          )}
          <CheckBox
            name="schumerEsign"
            value={formik.values.schumerEsign}
            setFieldValue={formik.setFieldValue}
            setFieldTouched={() => formik.setFieldTouched('schumerEsign')}
            disabled={formik.isSubmitting}
            error={formik.errors.schumerEsign}
            touched={formik.touched.schumerEsign}
            onChange={formik.handleChange}
            ariaLabelledBy="schumer-checkbox-text"
          >
            <SchumerEsignParagraphSection id="schumer-checkbox-text">
              <SchumerEsignParagraph>
                {isPendingUnconditionalOfferAccept()
                  ? `I, ${firstName} ${lastName}, agree by electronic signature to and acknowledge that I have read and understood the Cardholder Agreement.`
                  : 'I have read, understood, and agree to the Terms & Conditions.'}
              </SchumerEsignParagraph>
              {!isPendingUnconditionalOfferAccept() && (
                <SchumerEsignParagraph>
                  I understand that this is a pre-approved offer, which means
                  that credit is not guaranteed and my eligibility will need to
                  be verified. I understand and acknowledge that the Terms &amp;
                  Conditions are not effective until Prosper communicates a
                  final approval to me.
                </SchumerEsignParagraph>
              )}
            </SchumerEsignParagraphSection>
          </CheckBox>
        </EsignSection>
        <SubmitButton
          id="submit"
          aria-label="Agree to the Terms & Conditions and Continue"
          type="submit"
          disabled={formik.isSubmitting}
        >
          Agree &amp; Continue
        </SubmitButton>
      </StyledForm>
    </GradientCard>
  );
};
